/**
 * Editor modal + Gomag-link picker for Marketplace Offers.
 *
 * Two main components:
 *  - <MarketplaceOfferEditModal> — simplified editor: ONLY prices (with VAT,
 *    auto-calc no-VAT) and stock (BAU/Trendyol only — FBE is read-only) are
 *    editable. All other metadata (name, brand, EAN, URL, images, description)
 *    is read-only here and managed directly on the marketplace.
 *  - <GomagLinkModal> — popup over the offers table. Searches Gomag products
 *    by name/SKU/PNK/EAN and links the offer to the chosen Gomag product.
 *    After linking the stock-reconciler will use Gomag's stoc_oblio as the
 *    canonical inventory for that offer (BAU + Trendyol only).
 *  - <QuickPriceEditor> — inline popover for fast price + stock (BAU) edits.
 *
 * Endpoints used:
 *  - GET    /api/marketplace-offers/{integration_key}/{offer_id}
 *  - PUT    /api/marketplace-offers/{integration_key}/{offer_id}        (full)
 *  - PUT    /api/marketplace-offers/{integration_key}/{offer_id}/price  (quick)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Save, Send, Search, Link2, RefreshCw, ExternalLink,
  Package as PackageIcon, AlertCircle, CheckCircle2, Lock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FLAG_FOR = {
  emag_ro_bau: "🇷🇴 eMAG RO BAU", emag_bg_bau: "🇧🇬 eMAG BG BAU", emag_hu_bau: "🇭🇺 eMAG HU BAU",
  emag_ro_fbe: "🇷🇴 eMAG RO FBE", emag_bg_fbe: "🇧🇬 eMAG BG FBE", emag_hu_fbe: "🇭🇺 eMAG HU FBE",
  trendyol_ro: "🇷🇴 Trendyol RO", trendyol_bg: "🇧🇬 Trendyol BG",
};

// Strip HTML tags from a string for read-only plain-text rendering.
const stripHtml = (s) => {
  if (!s) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = s;
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
};

// ───────────────────────────────────────────────────────────────────────────
// Right-sidebar read-only badges
// ───────────────────────────────────────────────────────────────────────────
function SidebarRow({ label, value, tone, testId }) {
  const map = {
    green: "bg-emerald-500/15 text-emerald-200",
    amber: "bg-amber-500/15 text-amber-200",
    rose: "bg-rose-500/15 text-rose-200",
    zinc: "bg-zinc-800 text-zinc-200",
  };
  return (
    <div className="flex items-start justify-between border-b border-zinc-800 px-4 py-3 last:border-b-0">
      <span className="text-sm font-medium text-zinc-400">{label}:</span>
      {tone ? (
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[tone] || map.zinc}`} data-testid={testId}>
          {value}
        </span>
      ) : (
        <span className="text-sm font-mono text-sky-300 text-right break-all" data-testid={testId}>{value ?? "—"}</span>
      )}
    </div>
  );
}

function EditorSidebar({ offer, pushStatus }) {
  const stockTone = offer.stock > 0 ? "green" : "rose";
  const statusTone = offer.status === 1 ? "green" : "amber";
  const docTone = offer.ownership ? "green" : "zinc";
  return (
    <aside className="w-full max-w-sm flex-shrink-0 overflow-y-auto border-l border-zinc-800 bg-zinc-950/60" data-testid="offer-editor-sidebar">
      <div className="flex flex-col">
        <SidebarRow label="Marketplace" value={FLAG_FOR[offer.integration_key] || offer.integration_key} testId="sidebar-marketplace" />
        <SidebarRow label="ID" value={offer.external_id} testId="sidebar-id" />
        <SidebarRow label="SKU" value={offer.sku} testId="sidebar-sku" />
        {offer.pnk && <SidebarRow label="PNK" value={offer.pnk} testId="sidebar-pnk" />}
        <SidebarRow label="Stoc curent" value={`${offer.stock ?? 0}`} tone={stockTone} testId="sidebar-stock" />
        <SidebarRow label="Deținător documentație" value={offer.ownership ? "Da" : "—"} tone={docTone} testId="sidebar-ownership" />
        <SidebarRow label="Status" value={offer.status === 1 ? "Activ" : "Inactiv"} tone={statusTone} testId="sidebar-status" />
        {offer.linked_gomag_part_number && (
          <SidebarRow label="Linked Gomag" value={offer.linked_gomag_part_number} tone="green" testId="sidebar-linked-gomag" />
        )}
        {offer.url && (
          <div className="border-t border-zinc-800 px-4 py-3">
            <a href={offer.url} target="_blank" rel="noopener noreferrer"
              data-testid="sidebar-url-link"
              className="inline-flex items-center gap-1.5 text-xs text-sky-300 hover:text-sky-200">
              <ExternalLink className="h-3 w-3" /> Vezi pe marketplace
            </a>
          </div>
        )}
        {pushStatus && (
          <div className={`mx-3 my-3 rounded-md border p-2 text-[11px] ${
            pushStatus.applied ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" :
            pushStatus.attempted ? "border-amber-500/40 bg-amber-500/10 text-amber-200" :
            "border-zinc-800 bg-zinc-900 text-zinc-400"
          }`}>
            {pushStatus.applied ? "✓ Trimis cu succes la marketplace" :
             pushStatus.attempted ? `⚠ Răspuns marketplace: ${(pushStatus.error || "—").slice(0, 120)}` :
             "Salvat local — push neîncercat"}
          </div>
        )}
      </div>
    </aside>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Small building blocks
// ───────────────────────────────────────────────────────────────────────────
function Section({ title, hint, children }) {
  return (
    <section className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-950/30 p-4">
      <header>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-300">{title}</h3>
        {hint && <p className="mt-0.5 text-[10px] text-zinc-500">{hint}</p>}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function ReadOnlyField({ label, value, testId, mono = false }) {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
        <Lock className="h-2.5 w-2.5" /> {label}
      </label>
      <div
        data-testid={testId}
        className={`w-full rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-sm text-zinc-300 ${mono ? "font-mono" : ""}`}>
        {value || <span className="text-zinc-600">—</span>}
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, testId, readOnly, suffix, step = "0.01" }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">{label}</label>
      <div className="relative">
        <input type="number" step={step} value={value ?? ""}
          onChange={onChange && ((e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value)))}
          data-testid={testId} readOnly={readOnly}
          className={`w-full rounded-lg border px-3 py-2 text-sm text-zinc-100 outline-none ${
            readOnly ? "border-zinc-800 bg-zinc-900/40 text-zinc-400" : "border-zinc-800 bg-zinc-900 focus:border-orange-500/50"
          }`} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500">{suffix}</span>}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// MAIN EDITOR MODAL (simplified — prices + stock only)
// ───────────────────────────────────────────────────────────────────────────
export function MarketplaceOfferEditModal({ integrationKey, offerId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [offer, setOffer] = useState(null);
  const [form, setForm] = useState({});
  const [pushStatus, setPushStatus] = useState(null);

  const isFbe = integrationKey?.endsWith("_fbe");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/marketplace-offers/${integrationKey}/${offerId}`).then((r) => {
      setOffer(r.data);
      const d = r.data;
      setForm({
        sale_price_with_vat: d.sale_price_with_vat ?? null,
        recommended_price_with_vat: d.recommended_price_with_vat ?? null,
        min_sale_price: d.min_sale_price ?? null,
        max_sale_price: d.max_sale_price ?? null,
        stock: d.stock ?? 0,
      });
      setPushStatus(d._marketplace_push_status || null);
    }).catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [integrationKey, offerId]);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const vat = offer?.vat_rate ?? 0.21;

  const noVatPreview = (v) => {
    if (v == null || isNaN(v) || v === "") return null;
    return Math.round((Number(v) / (1 + vat)) * 100) / 100;
  };

  const save = async (push = true) => {
    setSaving(true); setError("");
    try {
      const payload = {
        sale_price_with_vat: form.sale_price_with_vat,
        recommended_price_with_vat: form.recommended_price_with_vat,
        min_sale_price: form.min_sale_price,
        max_sale_price: form.max_sale_price,
      };
      // Stock is editable only for BAU/Trendyol.
      if (!isFbe && form.stock !== null && form.stock !== undefined && form.stock !== "") {
        payload.stock = form.stock;
      }
      const r = await axios.put(`${API}/marketplace-offers/${integrationKey}/${offerId}?push=${push ? "true" : "false"}`, payload);
      setPushStatus(r.data?.marketplace_push || null);
      const applied = r.data?.marketplace_push?.applied;
      toast.success(push ? (applied ? "Salvat + push reușit" : "Salvat (push raportat eroare — vezi panou)") : "Salvat local");
      onSaved && onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      toast.error("Eroare la salvare: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <div className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950 px-5 py-3 text-sm text-zinc-300">
          <RefreshCw className="h-4 w-4 animate-spin" /> Se încarcă oferta...
        </div>
      </div>
    );
  }
  if (!offer) return null;

  const descText = stripHtml(offer.description);
  const firstImage = (offer.images || [])
    .map((x) => (typeof x === "string" ? x : x?.url))
    .filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex bg-black/70" onClick={onClose} data-testid="offer-editor-modal">
      <div className="ml-auto flex h-full w-full max-w-5xl flex-col bg-zinc-950" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <header className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-zinc-100" data-testid="editor-offer-name">{offer.name}</p>
            <p className="text-[11px] text-zinc-500">{FLAG_FOR[integrationKey]} · SKU <span className="font-mono text-sky-300">{offer.sku}</span> · ID <span className="font-mono text-sky-300">{offer.external_id}</span></p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => save(false)} disabled={saving}
              data-testid="editor-save-local"
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 hover:text-zinc-100 disabled:opacity-50">
              <Save className="h-3.5 w-3.5" /> {saving ? "..." : "Salvează local"}
            </button>
            <button onClick={() => save(true)} disabled={saving}
              data-testid="editor-save-push"
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
              <Send className="h-3.5 w-3.5" /> {saving ? "..." : "Salvează + Push"}
            </button>
            <button onClick={onClose} data-testid="editor-close"
              className="rounded-lg border border-zinc-700 bg-zinc-900 p-1.5 text-zinc-300 hover:text-zinc-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* BODY */}
        <div className="flex flex-1 overflow-hidden">
          <main className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="editor-main">
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5" /> {error}
              </div>
            )}

            <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 px-4 py-3 text-[11px] text-sky-200">
              <Lock className="mr-1 inline h-3 w-3" />
              Doar <strong>prețurile</strong> {!isFbe && <>și <strong>stocul</strong></>} se editează aici. Restul informațiilor (nume, descriere, imagini, EAN, GPSR, categorie) se gestionează direct pe marketplace pentru a evita suprascrieri accidentale.
            </div>

            <Section title="✏️ Prețuri (editabile)" hint={`Introduci prețurile CU TVA; aplicația calculează automat valoarea fără TVA (TVA ${(vat * 100).toFixed(0)}%) pentru push la eMAG.`}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <NumberField label="Preț redus (cu TVA)" value={form.sale_price_with_vat}
                    onChange={(v) => setF("sale_price_with_vat", v)}
                    testId="editor-sale-with-vat" suffix={offer.currency} />
                  <p className="mt-1 text-[10px] text-zinc-500">Fără TVA: <span className="font-mono text-zinc-300">{noVatPreview(form.sale_price_with_vat) ?? "—"}</span> {offer.currency}</p>
                </div>
                <div>
                  <NumberField label="Preț întreg (cu TVA)" value={form.recommended_price_with_vat}
                    onChange={(v) => setF("recommended_price_with_vat", v)}
                    testId="editor-rec-with-vat" suffix={offer.currency} />
                  <p className="mt-1 text-[10px] text-zinc-500">Fără TVA: <span className="font-mono text-zinc-300">{noVatPreview(form.recommended_price_with_vat) ?? "—"}</span> {offer.currency}</p>
                </div>
                <NumberField label="Preț minim" value={form.min_sale_price}
                  onChange={(v) => setF("min_sale_price", v)} testId="editor-min-price" suffix={offer.currency} />
                <NumberField label="Preț maxim" value={form.max_sale_price}
                  onChange={(v) => setF("max_sale_price", v)} testId="editor-max-price" suffix={offer.currency} />
              </div>
            </Section>

            <Section title={isFbe ? "📦 Stoc (read-only — gestionat de eMAG FBE)" : "📦 Stoc (editabil)"}
              hint={isFbe
                ? "Stocul FBE este sub controlul eMAG (depozitul lor). Nu poate fi editat aici."
                : "Implicit este stocul din produsul Gomag legat (Oblio − rezervări). Modificarea aici trimite stoc fix la marketplace."}>
              <NumberField
                label="Stoc"
                value={form.stock}
                onChange={isFbe ? undefined : (v) => setF("stock", v === null ? 0 : Math.max(0, Math.floor(v)))}
                step="1"
                testId="editor-stock"
                readOnly={isFbe}
                suffix="buc"
              />
            </Section>

            <Section title="🔒 Detalii produs (read-only)" hint="Aceste informații sunt gestionate direct pe marketplace.">
              <ReadOnlyField label="Nume" value={offer.name} testId="editor-name" />
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField label="Brand" value={offer.brand} testId="editor-brand" />
                <ReadOnlyField label="EAN" value={offer.ean} testId="editor-ean" mono />
              </div>
              <ReadOnlyField label="URL marketplace" value={offer.url} testId="editor-url" mono />

              <div>
                <label className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
                  <Lock className="h-2.5 w-2.5" /> Imagini ({firstImage.length})
                </label>
                {firstImage.length === 0 ? (
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-4 text-center text-xs text-zinc-600">Fără imagini</div>
                ) : (
                  <div className="grid grid-cols-4 gap-2" data-testid="editor-images-readonly">
                    {firstImage.slice(0, 8).map((img, i) => (
                      <div key={img + i} className="aspect-square overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900">
                        <img src={img} alt="" loading="lazy" className="h-full w-full object-contain" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
                  <Lock className="h-2.5 w-2.5" /> Descriere (text brut)
                </label>
                <div
                  data-testid="editor-description"
                  className="max-h-40 w-full overflow-y-auto whitespace-pre-wrap rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-sm text-zinc-300">
                  {descText || <span className="text-zinc-600">— fără descriere —</span>}
                </div>
              </div>
            </Section>

            <p className="text-[10px] text-zinc-600">⚠ Trendyol acceptă push doar pentru preț și stoc.</p>
          </main>

          <EditorSidebar offer={offer} pushStatus={pushStatus} />
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// GOMAG LINK PICKER MODAL
// ───────────────────────────────────────────────────────────────────────────
export function GomagLinkModal({ integrationKey, offerId, currentLink, prefillSku, prefillEan, prefillPnk, onClose, onLinked }) {
  const [search, setSearch] = useState(prefillSku || prefillEan || prefillPnk || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [matchHint, setMatchHint] = useState("");

  const runSearch = async (q, hint = "") => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/marketplace-offers/gomag-search`, { params: { q, limit: 30 } });
      setResults(r.data?.products || []);
      setMatchHint(hint);
      return r.data?.products || [];
    } catch (e) {
      toast.error("Eroare căutare Gomag: " + (e.response?.data?.detail || e.message));
      return [];
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      if (prefillSku) {
        const r = await runSearch(prefillSku, "SKU");
        if (r.length > 0) return;
      }
      if (prefillEan) {
        const r = await runSearch(prefillEan, "EAN");
        if (r.length > 0) return;
      }
      if (prefillPnk) {
        const r = await runSearch(prefillPnk, "PNK");
        if (r.length > 0) return;
      }
      await runSearch("");
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (search === (prefillSku || prefillEan || prefillPnk || "")) return;
    const t = setTimeout(() => runSearch(search, ""), 300);
    return () => clearTimeout(t);
  }, [search]);  // eslint-disable-line react-hooks/exhaustive-deps

  const doLink = async (gomag) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/marketplace-offers/${integrationKey}/${offerId}/link-gomag`,
        { gomag_product_id: gomag.id });
      toast.success(`Linkuit cu ${gomag.part_number}`);
      onLinked && onLinked(gomag);
      onClose && onClose();
    } catch (e) {
      toast.error("Eroare linking: " + (e.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  const unlink = async () => {
    if (!window.confirm("Sigur dezleg acest produs?")) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API}/marketplace-offers/${integrationKey}/${offerId}/link-gomag`);
      toast.success("Dezlegat");
      onLinked && onLinked(null);
      onClose && onClose();
    } catch (e) {
      toast.error("Eroare unlink: " + (e.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="gomag-link-modal">
      <div className="w-full max-w-2xl rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-zinc-100">Link cu produs Gomag</h3>
            <p className="text-[11px] text-zinc-500">Stocul ofertei va fi calculat automat din Gomag (Oblio − rezervări).</p>
          </div>
          <button onClick={onClose} data-testid="gomag-link-close" className="rounded p-1 text-zinc-400 hover:text-zinc-100">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-5">
          {currentLink && (
            <div className="mb-4 flex items-center justify-between rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200" data-testid="current-link-banner">
              <span>Linkuit cu <span className="font-mono font-bold">{currentLink}</span></span>
              <button onClick={unlink} disabled={submitting} data-testid="gomag-link-unlink"
                className="rounded border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[11px] text-rose-200 hover:bg-rose-500/20">
                Dezleagă
              </button>
            </div>
          )}
          {matchHint && results.length > 0 && (
            <div className="mb-3 rounded-lg border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-[11px] text-sky-200" data-testid="prefill-hint">
              💡 Am găsit {results.length} potriviri după <strong>{matchHint}</strong> ({prefillSku || prefillEan || prefillPnk}). Prima sugestie e marcată mai jos — un click linkuiește.
            </div>
          )}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="gomag-link-search"
              autoFocus placeholder="Caută după nume, SKU, PNK sau EAN..."
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-9 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                title="Curăță">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto" data-testid="gomag-link-results">
            {loading && <p className="px-3 py-3 text-xs text-zinc-500">Se caută...</p>}
            {!loading && results.length === 0 && (
              <p className="px-3 py-6 text-center text-xs text-zinc-500">Niciun rezultat. Schimbă termenii de căutare.</p>
            )}
            {!loading && results.map((p, idx) => {
              const isTopMatch = idx === 0 && matchHint;
              return (
                <button key={p.id} onClick={() => doLink(p)} disabled={submitting}
                  data-testid={`gomag-link-result-${p.id}`}
                  className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition disabled:opacity-50 ${
                    isTopMatch
                      ? "border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10"
                      : "border-transparent hover:border-orange-500/30 hover:bg-zinc-900"
                  }`}>
                  <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border border-zinc-800 bg-zinc-900">
                    {p.main_image ? <img src={p.main_image} alt="" className="h-full w-full object-contain" loading="lazy" />
                      : <div className="flex h-full w-full items-center justify-center text-zinc-700"><PackageIcon className="h-5 w-5" /></div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {isTopMatch && <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-200">Sugerat</span>}
                      <p className="truncate text-xs font-semibold text-zinc-100" title={p.name}>{p.name}</p>
                    </div>
                    <p className="font-mono text-[10px] text-sky-300">{p.part_number}</p>
                  </div>
                  <div className="text-right text-[10px] text-zinc-400">
                    <p>Stoc Oblio: <span className="font-mono font-semibold text-emerald-300">{p.stoc_oblio ?? "—"}</span></p>
                    {p.ean && <p>EAN: <span className="font-mono">{Array.isArray(p.ean) ? p.ean[0] : p.ean}</span></p>}
                  </div>
                  <CheckCircle2 className={`h-4 w-4 ${isTopMatch ? "text-emerald-400" : "text-zinc-600"}`} />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// QUICK PRICE + STOCK EDIT POPOVER (inline from offers table)
// ───────────────────────────────────────────────────────────────────────────
export function QuickPriceEditor({ integrationKey, offerId, currentPrice, currentStock, currency, vat, onClose, onSaved }) {
  const isFbe = integrationKey?.endsWith("_fbe");
  const [pwv, setPwv] = useState(currentPrice ?? "");
  const [stock, setStock] = useState(currentStock ?? "");
  const [saving, setSaving] = useState(false);
  const v = vat ?? 0.21;
  const pnv = pwv === "" || pwv === null ? null : Math.round((Number(pwv) / (1 + v)) * 100) / 100;

  const save = async () => {
    if ((pwv === "" || pwv === null) && (stock === "" || stock === null || isFbe)) {
      toast.error("Introdu un preț sau un stoc"); return;
    }
    setSaving(true);
    try {
      const body = {};
      if (pwv !== "" && pwv !== null) body.price_with_vat = Number(pwv);
      if (!isFbe && stock !== "" && stock !== null) body.stock = Math.max(0, parseInt(stock, 10) || 0);
      const r = await axios.put(`${API}/marketplace-offers/${integrationKey}/${offerId}/price`, body);
      const pushed = r.data?.marketplace_push?.applied;
      toast.success(pushed ? "Actualizat + push la marketplace" : "Salvat local");
      onSaved && onSaved(r.data);
      onClose && onClose();
    } catch (e) {
      toast.error("Eroare salvare: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="quick-price-modal">
      <div className="w-full max-w-sm rounded-xl border border-zinc-800 bg-zinc-950 p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100">Editare rapidă preț {!isFbe && "+ stoc"}</h3>
          <button onClick={onClose} data-testid="quick-price-close" className="text-zinc-400 hover:text-zinc-100"><X className="h-4 w-4" /></button>
        </div>
        <NumberField label={`Preț cu TVA (${currency})`} value={pwv} onChange={setPwv} testId="quick-price-input" suffix={currency} />
        <p className="mt-1 text-[10px] text-zinc-500">Preț fără TVA: {pnv ?? "—"} {currency} (calculat la TVA {(v * 100).toFixed(0)}%)</p>

        <div className="mt-3">
          <NumberField
            label={isFbe ? "Stoc (read-only — FBE)" : "Stoc"}
            value={stock}
            onChange={isFbe ? undefined : setStock}
            testId="quick-stock-input"
            readOnly={isFbe}
            step="1"
            suffix="buc"
          />
          {isFbe && <p className="mt-1 text-[10px] text-zinc-500">Stocul FBE este gestionat de eMAG.</p>}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300">Anulează</button>
          <button onClick={save} disabled={saving} data-testid="quick-price-save"
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
            <Send className="h-3.5 w-3.5" /> {saving ? "Se trimite..." : "Salvează + Push"}
          </button>
        </div>
      </div>
    </div>
  );
}
